import React, { useState, useEffect } from 'react';
import { Button } from 'primereact/button';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Message } from 'primereact/message';
import { Avatar } from 'primereact/avatar';
import { Tag } from 'primereact/tag';
import apiClient from '../../stateManagers/apiSetup/axiosInterceptor';

interface OperatorData {
  user_id: string;
  name: string;
  email: string;
  role: string;
  assigned_at: string;
}

interface BranchData {
  branch_id: string;
  institute_id: string;
  name: string;
  license_number: string;
  license_start: string;
  license_expiry: string;
  address: string;
  pincode: number;
  total_pc: number;
  latitude: number;
  longitude: number;
  opening_time: string;
  closing_time: string;
  capacity: number;
  status: string;
  created_at: string;
}

interface InstituteData {
  institute_id: string;
  name: string;
  address: string;
  contact_no: string;
  email: string;
  created_at: string;
}

interface ApiResponse {
  success: boolean;
  data: {
    operator: OperatorData;
    branch: BranchData;
    institute: InstituteData;
  };
}

const InstituteProfile: React.FC = () => {
  const [profileData, setProfileData] = useState<ApiResponse['data'] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      setError(null);
      

      const response = await apiClient.get<ApiResponse>(`/v1/branches/get-branch-profile`);
      
      if (response.data.success) {
        setProfileData(response.data.data);
      } else {
        setError('Failed to load profile');
      }
    } catch (err: any) {
      console.error('Failed to fetch profile:', err);
      setError(err.message || 'Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  const formatTime = (timeString: string) => {
    try {
      const [hours, minutes] = timeString.split(':');
      const hour = parseInt(hours);
      const ampm = hour >= 12 ? 'PM' : 'AM';
      const displayHour = hour % 12 || 12;
      return `${displayHour}:${minutes} ${ampm}`;
    } catch {
      return timeString;
    }
  };

  const calculateLicenseDaysRemaining = (expiryDate: string) => {
    const expiry = new Date(expiryDate).getTime();
    const now = Date.now();
    const daysRemaining = Math.ceil((expiry - now) / (1000 * 60 * 60 * 24));
    return daysRemaining;
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '400px',
        background: '#f5f5f5',
        borderRadius: '12px'
      }}>
        <div style={{ textAlign: 'center' }}>
          <ProgressSpinner 
            style={{ width: '50px', height: '50px' }} 
            strokeWidth="3"
          />
          <p style={{
            marginTop: '16px',
            color: '#0f3d3e',
            fontSize: '0.95rem'
          }}>
            Loading profile...
          </p>
        </div>
      </div>
    );
  }

  if (error || !profileData) {
    return (
      <div>
        <Message 
          severity="error" 
          text={error || 'Profile not found'}
        />
      </div>
    );
  }

  const { operator, branch, institute } = profileData;
  const daysRemaining = calculateLicenseDaysRemaining(branch.license_expiry);
  const isLicenseExpiring = daysRemaining < 30 && daysRemaining > 0;
  const isLicenseExpired = daysRemaining <= 0;

  return (
    <div style={{ padding: '0' }}>
      {/* Header Section with Hero Banner */}
      <div style={{
        background: 'linear-gradient(135deg, #0f3d3e 0%, #1a5555 50%, #0a2f2f 100%)',
        borderRadius: '20px',
        padding: '32px',
        marginBottom: '24px',
        color: 'white',
        position: 'relative',
        overflow: 'hidden',
        boxShadow: '0 10px 40px rgba(15, 61, 62, 0.3)'
      }}>
        {/* Decorative elements */}
        <div style={{
          position: 'absolute',
          top: '-50px',
          right: '-50px',
          width: '200px',
          height: '200px',
          background: 'rgba(212, 241, 126, 0.1)',
          borderRadius: '50%',
          filter: 'blur(40px)'
        }} />
        <div style={{
          position: 'absolute',
          bottom: '-80px',
          left: '-80px',
          width: '250px',
          height: '250px',
          background: 'rgba(212, 241, 126, 0.08)',
          borderRadius: '50%',
          filter: 'blur(50px)'
        }} />
        
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          flexWrap: 'wrap',
          gap: '24px',
          position: 'relative',
          zIndex: 1
        }}>
          <div style={{ flex: 1, minWidth: '250px' }}>
            <div style={{
              display: 'inline-block',
              padding: '5px 12px',
              background: 'rgba(212, 241, 126, 0.2)',
              borderRadius: '20px',
              marginBottom: '10px',
              fontSize: '0.7rem',
              fontWeight: '600',
              letterSpacing: '0.5px',
              textTransform: 'uppercase'
            }}>
              Institute Profile
            </div>
            <h1 style={{
              margin: '0 0 8px 0',
              fontSize: '1.7rem',
              fontWeight: '700',
              color: 'white',
              letterSpacing: '-0.5px'
            }}>
              {institute.name}
            </h1>
            <p style={{
              margin: '0 0 20px 0',
              color: 'rgba(255,255,255,0.8)',
              fontSize: '0.9rem'
            }}>
              {institute.address}
            </p>

            {/* Quick Stats */}
            <div style={{
              display: 'flex',
              gap: '16px',
              flexWrap: 'wrap'
            }}>
              <div style={{
                background: 'rgba(255,255,255,0.1)',
                backdropFilter: 'blur(10px)',
                padding: '12px 16px',
                borderRadius: '12px',
                border: '1px solid rgba(255,255,255,0.2)'
              }}>
                <p style={{
                  margin: '0 0 4px 0',
                  fontSize: '0.7rem',
                  color: 'rgba(255,255,255,0.7)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  Contact
                </p>
                <p style={{
                  margin: 0,
                  fontSize: '0.9rem',
                  fontWeight: '600',
                  color: '#d4f17e'
                }}>
                  {institute.contact_no}
                </p>
              </div>

              <div style={{
                background: 'rgba(255,255,255,0.1)',
                backdropFilter: 'blur(10px)',
                padding: '12px 16px',
                borderRadius: '12px',
                border: '1px solid rgba(255,255,255,0.2)'
              }}>
                <p style={{
                  margin: '0 0 4px 0',
                  fontSize: '0.7rem',
                  color: 'rgba(255,255,255,0.7)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  Email
                </p>
                <p style={{
                  margin: 0,
                  fontSize: '0.9rem',
                  fontWeight: '600',
                  color: '#d4f17e'
                }}>
                  {institute.email}
                </p>
              </div>
            </div>
          </div>

          <div style={{ textAlign: 'right' }}>
            <p style={{
              margin: '0',
              fontSize: '0.75rem',
              color: 'rgba(255,255,255,0.6)',
              fontFamily: 'monospace'
            }}>
              ID: {institute.institute_id}
            </p>
            <p style={{
              margin: '8px 0 0 0',
              fontSize: '0.75rem',
              color: 'rgba(255,255,255,0.5)'
            }}>
              Since {formatDate(institute.created_at)}
            </p>
          </div>
        </div>
      </div>

      {/* License Warning */}
      {(isLicenseExpiring || isLicenseExpired) && (
        <Message
          severity={isLicenseExpired ? 'error' : 'warn'}
          text={
            isLicenseExpired
              ? `⚠️ Branch license expired ${Math.abs(daysRemaining)} days ago - Immediate action required`
              : `⏰ Branch license expiring in ${daysRemaining} days - Please renew soon`
          }
          style={{ 
            marginBottom: '20px',
            fontSize: '0.85rem',
            fontWeight: '500'
          }}
        />
      )}

      {/* Main Content - Two Column Layout */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '2fr 1fr',
        gap: '20px',
        marginBottom: '24px'
      }}>
        {/* Left Column - Main Information */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          {/* Branch Information Section */}
          <div style={{
            background: 'white',
            borderRadius: '16px',
            padding: '24px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
            border: '1px solid #f0f0f0'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              marginBottom: '20px'
            }}>
              <div style={{
                width: '40px',
                height: '40px',
                background: 'linear-gradient(135deg, #0f3d3e 0%, #1a5555 100%)',
                borderRadius: '10px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <i className="pi pi-building" style={{ color: '#d4f17e', fontSize: '1.1rem' }}></i>
              </div>
              <div>
                <h2 style={{
                  margin: 0,
                  fontSize: '1.1rem',
                  fontWeight: '700',
                  color: '#0f3d3e'
                }}>
                  Your Branch
                </h2>
                <p style={{
                  margin: '2px 0 0 0',
                  fontSize: '0.75rem',
                  color: '#999'
                }}>
                  Current branch assignment
                </p>
              </div>
            </div>

            <div style={{
              background: '#f8f9fa',
              padding: '16px',
              borderRadius: '10px',
              marginBottom: '16px'
            }}>
              <p style={{
                margin: '0 0 6px 0',
                fontSize: '0.7rem',
                color: '#666',
                fontWeight: '600',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                Branch Name
              </p>
              <p style={{
                margin: 0,
                fontSize: '1.2rem',
                fontWeight: '700',
                color: '#0f3d3e'
              }}>
                {branch.name}
              </p>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                marginTop: '8px'
              }}>
                <Tag
                  value={branch.status.toUpperCase()}
                  severity={branch.status === 'active' ? 'success' : 'danger'}
                  style={{
                    fontSize: '0.65rem',
                    fontWeight: '600',
                    padding: '4px 10px'
                  }}
                />
                <p style={{
                  margin: 0,
                  fontSize: '0.75rem',
                  color: '#666'
                }}>
                  Capacity: {branch.capacity} | PCs: {branch.total_pc}
                </p>
              </div>
            </div>

            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '12px'
            }}>
              <div>
                <p style={{
                  margin: '0 0 6px 0',
                  fontSize: '0.7rem',
                  color: '#999',
                  fontWeight: '600',
                  textTransform: 'uppercase'
                }}>
                  Opening Time
                </p>
                <p style={{
                  margin: 0,
                  fontSize: '0.9rem',
                  fontWeight: '600',
                  color: '#333'
                }}>
                  {formatTime(branch.opening_time)}
                </p>
              </div>

              <div>
                <p style={{
                  margin: '0 0 6px 0',
                  fontSize: '0.7rem',
                  color: '#999',
                  fontWeight: '600',
                  textTransform: 'uppercase'
                }}>
                  Closing Time
                </p>
                <p style={{
                  margin: 0,
                  fontSize: '0.9rem',
                  fontWeight: '600',
                  color: '#333'
                }}>
                  {formatTime(branch.closing_time)}
                </p>
              </div>
            </div>
          </div>

          {/* License Information Section */}
          <div style={{
            background: 'white',
            borderRadius: '16px',
            padding: '24px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
            border: '1px solid #f0f0f0'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              marginBottom: '20px'
            }}>
              <div style={{
                width: '40px',
                height: '40px',
                background: 'linear-gradient(135deg, #0f3d3e 0%, #1a5555 100%)',
                borderRadius: '10px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <i className="pi pi-id-card" style={{ color: '#d4f17e', fontSize: '1.1rem' }}></i>
              </div>
              <div>
                <h2 style={{
                  margin: 0,
                  fontSize: '1.1rem',
                  fontWeight: '700',
                  color: '#0f3d3e'
                }}>
                  License Information
                </h2>
                <p style={{
                  margin: '2px 0 0 0',
                  fontSize: '0.75rem',
                  color: '#999'
                }}>
                  Branch license details and validity
                </p>
              </div>
            </div>

            <div style={{
              background: '#f8f9fa',
              padding: '16px',
              borderRadius: '10px',
              marginBottom: '16px'
            }}>
              <p style={{
                margin: '0 0 6px 0',
                fontSize: '0.7rem',
                color: '#666',
                fontWeight: '600',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                License Number
              </p>
              <p style={{
                margin: 0,
                fontSize: '1.2rem',
                fontWeight: '700',
                color: '#0f3d3e',
                fontFamily: 'monospace',
                letterSpacing: '1px'
              }}>
                {branch.license_number}
              </p>
            </div>

            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr 1fr',
              gap: '12px'
            }}>
              <div>
                <p style={{
                  margin: '0 0 6px 0',
                  fontSize: '0.7rem',
                  color: '#999',
                  fontWeight: '600',
                  textTransform: 'uppercase'
                }}>
                  Start Date
                </p>
                <p style={{
                  margin: 0,
                  fontSize: '0.85rem',
                  fontWeight: '600',
                  color: '#333'
                }}>
                  {formatDate(branch.license_start)}
                </p>
              </div>

              <div>
                <p style={{
                  margin: '0 0 6px 0',
                  fontSize: '0.7rem',
                  color: '#999',
                  fontWeight: '600',
                  textTransform: 'uppercase'
                }}>
                  Expiry Date
                </p>
                <p style={{
                  margin: 0,
                  fontSize: '0.85rem',
                  fontWeight: '700',
                  color: isLicenseExpired ? '#dc3545' : isLicenseExpiring ? '#ff9800' : '#4caf50'
                }}>
                  {formatDate(branch.license_expiry)}
                </p>
              </div>

              <div>
                <p style={{
                  margin: '0 0 6px 0',
                  fontSize: '0.7rem',
                  color: '#999',
                  fontWeight: '600',
                  textTransform: 'uppercase'
                }}>
                  Days Remaining
                </p>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}>
                  <i
                    className={`pi ${isLicenseExpired ? 'pi-times-circle' : isLicenseExpiring ? 'pi-exclamation-triangle' : 'pi-check-circle'}`}
                    style={{
                      fontSize: '1rem',
                      color: isLicenseExpired ? '#dc3545' : isLicenseExpiring ? '#ff9800' : '#4caf50'
                    }}
                  ></i>
                  <p style={{
                    margin: 0,
                    fontSize: '0.95rem',
                    fontWeight: '700',
                    color: isLicenseExpired ? '#dc3545' : isLicenseExpiring ? '#ff9800' : '#4caf50'
                  }}>
                    {daysRemaining > 0 ? daysRemaining : 'Expired'}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Location Section */}
          <div style={{
            background: 'white',
            borderRadius: '16px',
            padding: '24px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
            border: '1px solid #f0f0f0'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              marginBottom: '20px'
            }}>
              <div style={{
                width: '40px',
                height: '40px',
                background: 'linear-gradient(135deg, #0f3d3e 0%, #1a5555 100%)',
                borderRadius: '10px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <i className="pi pi-map-marker" style={{ color: '#d4f17e', fontSize: '1.1rem' }}></i>
              </div>
              <div>
                <h2 style={{
                  margin: 0,
                  fontSize: '1.1rem',
                  fontWeight: '700',
                  color: '#0f3d3e'
                }}>
                  Branch Location
                </h2>
                <p style={{
                  margin: '2px 0 0 0',
                  fontSize: '0.75rem',
                  color: '#999'
                }}>
                  Address and coordinates
                </p>
              </div>
            </div>

            <div style={{
              background: '#f8f9fa',
              padding: '16px',
              borderRadius: '10px',
              marginBottom: '16px'
            }}>
              <p style={{
                margin: '0 0 6px 0',
                fontSize: '0.7rem',
                color: '#666',
                fontWeight: '600',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                Full Address
              </p>
              <p style={{
                margin: 0,
                fontSize: '0.95rem',
                color: '#333',
                lineHeight: '1.5',
                fontWeight: '500'
              }}>
                {branch.address}
              </p>
              <p style={{
                margin: '6px 0 0 0',
                fontSize: '0.9rem',
                fontWeight: '700',
                color: '#0f3d3e'
              }}>
                Pincode: {branch.pincode}
              </p>
            </div>

            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '12px',
              padding: '12px',
              background: 'linear-gradient(135deg, rgba(15, 61, 62, 0.05) 0%, rgba(26, 85, 85, 0.05) 100%)',
              borderRadius: '10px'
            }}>
              <div>
                <p style={{
                  margin: '0 0 4px 0',
                  fontSize: '0.7rem',
                  color: '#666',
                  fontWeight: '600',
                  textTransform: 'uppercase'
                }}>
                  Latitude
                </p>
                <p style={{
                  margin: 0,
                  fontSize: '0.9rem',
                  fontWeight: '700',
                  color: '#0f3d3e',
                  fontFamily: 'monospace'
                }}>
                  {branch.latitude}°
                </p>
              </div>
              <div>
                <p style={{
                  margin: '0 0 4px 0',
                  fontSize: '0.7rem',
                  color: '#666',
                  fontWeight: '600',
                  textTransform: 'uppercase'
                }}>
                  Longitude
                </p>
                <p style={{
                  margin: 0,
                  fontSize: '0.9rem',
                  fontWeight: '700',
                  color: '#0f3d3e',
                  fontFamily: 'monospace'
                }}>
                  {branch.longitude}°
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column - Operator Info */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          {/* Your Profile */}
          <div style={{
            background: 'white',
            borderRadius: '16px',
            padding: '20px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
            border: '1px solid #f0f0f0'
          }}>
            <h3 style={{
              margin: '0 0 16px 0',
              fontSize: '1rem',
              fontWeight: '700',
              color: '#0f3d3e'
            }}>
              Your Profile
            </h3>

            <div>
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                padding: '20px',
                background: 'linear-gradient(135deg, #0f3d3e 0%, #1a5555 100%)',
                borderRadius: '12px',
                marginBottom: '16px'
              }}>
                <Avatar
                  label={operator.name.charAt(0)}
                  size="xlarge"
                  style={{
                    backgroundColor: '#d4f17e',
                    color: '#0f3d3e',
                    width: '64px',
                    height: '64px',
                    fontSize: '1.6rem',
                    fontWeight: '700',
                    marginBottom: '12px'
                  }}
                  shape="circle"
                />
                <p style={{
                  margin: '0 0 6px 0',
                  fontSize: '1.1rem',
                  fontWeight: '700',
                  color: 'white',
                  textAlign: 'center'
                }}>
                  {operator.name}
                </p>
                <p style={{
                  margin: '0 0 10px 0',
                  fontSize: '0.8rem',
                  color: 'rgba(255,255,255,0.8)',
                  textAlign: 'center',
                  wordBreak: 'break-all'
                }}>
                  {operator.email}
                </p>
                <Tag
                  value={operator.role.toUpperCase()}
                  style={{
                    background: '#d4f17e',
                    color: '#0f3d3e',
                    fontSize: '0.7rem',
                    padding: '5px 10px',
                    fontWeight: '700'
                  }}
                />
              </div>

              <div style={{ marginBottom: '12px' }}>
                <p style={{
                  margin: '0 0 6px 0',
                  fontSize: '0.7rem',
                  color: '#999',
                  fontWeight: '600',
                  textTransform: 'uppercase'
                }}>
                  User ID
                </p>
                <p style={{
                  margin: 0,
                  fontSize: '0.8rem',
                  fontWeight: '600',
                  color: '#0f3d3e',
                  fontFamily: 'monospace',
                  background: '#f8f9fa',
                  padding: '8px 10px',
                  borderRadius: '8px',
                  wordBreak: 'break-all'
                }}>
                  {operator.user_id}
                </p>
              </div>

              <div>
                <p style={{
                  margin: '0 0 6px 0',
                  fontSize: '0.7rem',
                  color: '#999',
                  fontWeight: '600',
                  textTransform: 'uppercase'
                }}>
                  Assigned On
                </p>
                <p style={{
                  margin: 0,
                  fontSize: '0.8rem',
                  color: '#333',
                  fontWeight: '500'
                }}>
                  {formatDate(operator.assigned_at)}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div style={{
        background: 'white',
        borderRadius: '16px',
        padding: '20px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
        border: '1px solid #f0f0f0',
        display: 'flex',
        gap: '12px',
        flexWrap: 'wrap',
        justifyContent: 'center'
      }}>
        <Button
          label="View Reports"
          icon="pi pi-chart-line"
          style={{
            background: '#0f3d3e',
            border: 'none',
            padding: '12px 24px',
            fontSize: '0.85rem',
            fontWeight: '600'
          }}
        />
        <Button
          label="Manage Students"
          icon="pi pi-users"
          outlined
          style={{
            color: '#0f3d3e',
            borderColor: '#0f3d3e',
            padding: '12px 24px',
            fontSize: '0.85rem',
            fontWeight: '600'
          }}
        />
        <Button
          label="Settings"
          icon="pi pi-cog"
          outlined
          style={{
            color: '#0f3d3e',
            borderColor: '#0f3d3e',
            padding: '12px 24px',
            fontSize: '0.85rem',
            fontWeight: '600'
          }}
        />
      </div>
    </div>
  );
};

export default InstituteProfile;